import { z } from "zod";

export const payoutStatusSchema = z.enum([
  "PrePayout",
  "PayingOutNote",
  "WaitingForNoteRemoval",
  "PayingOutCoin",
  "Success",
  "Error",
]);

export type PayoutStatus = z.infer<typeof payoutStatusSchema>;

export const cashPaymentDisplayStatusSchema = z.enum([
  "TOP_UP_IN_PROGRESS",
  "PAYMENT_IN_PROGRESS",
  "RECENTLY_FINISHED",
  "TIMED_OUT",
  "ABORTED",
  "HARDWARE_ERROR",
  "ABORTED_BUT_PAYOUT_FAILED",
  "SUCCESS_BUT_PAYOUT_FAILED",
]);

export type CashPaymentDisplayStatus = z.infer<
  typeof cashPaymentDisplayStatusSchema
>;

const cashPaymentEventSchema = z.object({
  type: z.string(),
  data: z.unknown().optional(),
});

const cashPaymentResultSchema = z.object({
  total: z.number(),
  changeAmount: z.number(),
  payoutAmount: z.number(),
  balanceCreditAmount: z.number(),
  entered: z.object({
    total: z.number(),
    byDenominations: z.record(z.string(), z.number()),
  }),
  dispensed: z.object({
    total: z.number(),
    byDenominations: z.record(z.string(), z.number()),
  }),
});

export const cashPaymentStatusSchema = z.object({
  paymentKey: z.string(),
  kind: z.enum(["purchase", "balance_topup"]),
  phase: z.enum(["pay-in", "pay-out"]),
  hasEnded: z.boolean(),
  resultCode: z.enum([
    "pending",
    "success",
    "aborted",
    "timed_out",
    "fatal_error",
    "aborted_but_payout_failed",
    "success_but_payout_failed",
  ]),
  requestedAmount: z.number().nullable(),
  insertedAmount: z.number(),
  dispensedAmount: z.number(),
  remainingAmount: z.number(),
  changeAmount: z.number(),
  creditedAmount: z.number(),
  payoutMode: z.enum(["change", "refund", "none"]).nullable(),
  acceptedCoinValues: z.array(z.number()),
  acceptedNoteValues: z.array(z.number()),
  payInState: z.enum(["starting", "active", "ending", "ended"]),
  payOutState: z.enum(["starting", "active", "ended", "failed"]).nullable(),
  // Which leg of the (NV11) two-leg pay-out is running: notes from the recycler
  // first, then coins. Absent/null on coin-only devices and older agents.
  payOutLeg: z
    .enum(["note", "coin"])
    .nullish()
    .catch(() => null),
  // True while a dispensed note sits in the bezel waiting to be taken.
  awaitingNoteRemoval: z
    .boolean()
    .nullish()
    .catch(() => false),
  sessionId: z
    .string()
    .optional()
    .catch(() => undefined),
  transactionId: z
    .string()
    .optional()
    .catch(() => undefined),
  printFailed: z.boolean(),
  sideEffects: z.object({
    inventoryPersisted: z.boolean(),
    balanceApplied: z.boolean(),
    machineStarted: z.boolean(),
    receiptPrinted: z.boolean(),
    backendEnqueued: z.boolean(),
    completed: z.boolean(),
  }),
  timeoutAt: z
    .string()
    .transform((d) => new Date(d))
    .catch(() => new Date()),
  result: cashPaymentResultSchema.nullable(),
  debug: z.object({
    payInEvents: z.array(cashPaymentEventSchema),
    payOutEvents: z.array(cashPaymentEventSchema),
  }),
  payment: z.unknown(),
});

export type CashPaymentStatus = z.infer<typeof cashPaymentStatusSchema>;

export type CashPaymentStatusView = CashPaymentStatus & {
  paymentStatus: CashPaymentDisplayStatus;
  payoutStatus: PayoutStatus | undefined;
  maxNote: number;
};

export function mapCashPaymentStatus(
  status: CashPaymentStatus,
): CashPaymentStatusView {
  return {
    ...status,
    paymentStatus: getCashPaymentDisplayStatus(status),
    payoutStatus: getPayoutStatus(status),
    maxNote: Math.max(0, ...status.acceptedNoteValues),
  };
}

function getCashPaymentDisplayStatus(
  status: CashPaymentStatus,
): CashPaymentDisplayStatus {
  switch (status.resultCode) {
    case "success":
      return "RECENTLY_FINISHED";
    case "aborted":
      return "ABORTED";
    case "timed_out":
      return "TIMED_OUT";
    case "fatal_error":
      return "HARDWARE_ERROR";
    case "aborted_but_payout_failed":
      return "ABORTED_BUT_PAYOUT_FAILED";
    case "success_but_payout_failed":
      return "SUCCESS_BUT_PAYOUT_FAILED";
    case "pending":
    default:
      return status.kind === "balance_topup"
        ? "TOP_UP_IN_PROGRESS"
        : "PAYMENT_IN_PROGRESS";
  }
}

export function getPayoutStatus(
  status: CashPaymentStatus,
): PayoutStatus | undefined {
  if (!status.payOutState) {
    return undefined;
  }

  switch (status.payOutState) {
    case "starting":
    case "active":
      // The note (recycler) leg runs first and dispenses notes one at a time,
      // pausing for the customer to take each note from the bezel; the coin leg
      // follows. Coin-only devices report no leg, so default to coins.
      if (status.payOutLeg === "note") {
        return status.awaitingNoteRemoval
          ? "WaitingForNoteRemoval"
          : "PayingOutNote";
      }
      return "PayingOutCoin";
    case "ended":
      return "Success";
    case "failed":
      return "Error";
    default:
      return "PrePayout";
  }
}
